import { BaseService } from "../BaseService";

export interface PushProviderStatus {
  provider: "fcm" | "web_push" | "apns";
  is_configured: boolean;
  is_active: boolean;
  metadata: Record<string, unknown> | null;
  updated_at: string | null;
}

export interface PushCredentialsResponse {
  providers: PushProviderStatus[];
}

export interface PushDevice {
  id: number;
  provider: "fcm" | "web_push" | "apns";
  token_preview: string;
  user_id: number | null;
  owner_entity_name: string | null;
  owner_entity_id: number | null;
  last_seen_at: string;
  unregistered_at: string | null;
  created_at: string;
}

export interface PushDevicesResponse {
  devices: PushDevice[];
}

export interface DeviceSourceConfig {
  kind: "tenant_devices" | "entity_field";
  entity?: string;
  token_field?: string;
  owner_user_field?: string;
  owner_entity_field?: string;
  owner_self?: boolean;
}

export interface PushCategory {
  id: number;
  category: string;
  label?: string | null;
  description?: string | null;
  is_enabled: boolean;
  ios_action_order?: string[] | null;
  ios_labels?: Record<string, string> | null;
  registered_at_version?: string | null;
  updated_at: string;
}

export interface PushCategoriesResponse {
  categories: PushCategory[];
}

export interface PushTemplateButton {
  id: string;
  label: string;
  click_action?: string | null;
}

export interface PushTemplate {
  id: number;
  template_type: string;
  name?: string | null;
  description?: string | null;
  title: string;
  body: string;
  image_url?: string | null;
  click_action?: string | null;
  category?: string | null;
  buttons?: PushTemplateButton[] | null;
  is_active: boolean;
  updated_at: string;
}

export interface PushTemplatesResponse {
  templates: PushTemplate[];
}

export type PushSendStatus = "Pending" | "Running" | "Succeeded" | "Partial" | "Failed" | "Unknown";

export interface PushSendLog {
  id: number;
  status: PushSendStatus;
  sent_by_user_id: number | null;
  source: string;
  workflow_id: number | null;
  workflow_name: string | null;
  step_key: string | null;
  title: string | null;
  body_preview: string | null;
  category: string | null;
  template_type: string | null;
  audience_kind: string;
  audience_summary: string | null;
  per_provider_json: string | null;
  error_message: string | null;
  queued_at: string;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PushSendLogsPage {
  items: PushSendLog[];
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

export interface CategoryReferenceRow {
  id: number;
  kind: string;
  name: string | null;
  workflow_id: number;
  step_key: string | null;
}

export interface CategoryReferencesResponse {
  category: string;
  templates: CategoryReferenceRow[];
  workflow_triggers: CategoryReferenceRow[];
  workflow_steps: CategoryReferenceRow[];
}

export interface RenameCategoryResponse {
  id: number;
  category: string;
  templates_cascaded: number;
  dangling: {
    workflow_triggers: CategoryReferenceRow[];
    workflow_steps: CategoryReferenceRow[];
  };
}

export interface SaveTemplateInput {
  template_type: string;
  name?: string;
  description?: string;
  title: string;
  body: string;
  image_url?: string;
  click_action?: string;
  category?: string;
  // Same row shape as the workflow / Send UI uses so we can pass directly into
  // PushButtonsField + IosButtonGroupPicker without remapping.
  buttons?: Array<{ id: string; label: string; click_action?: string }>;
  is_active: boolean;
}

export class TenantPushService extends BaseService {
  public async getCredentials(): Promise<PushCredentialsResponse> {
    return this.getRequest<PushCredentialsResponse>("/push/credentials");
  }

  public async saveFcm(serviceAccountJson: string): Promise<{ project_id: string; client_email: string }> {
    return this.putRequest<
      { service_account_json: string },
      { project_id: string; client_email: string }
    >(
      "/push/credentials/fcm",
      { service_account_json: serviceAccountJson },
      undefined,
      true,
      true,
    );
  }

  public async saveWebPush(subject: string): Promise<{ subject: string; public_key: string }> {
    return this.putRequest<
      { subject: string },
      { subject: string; public_key: string }
    >(
      "/push/credentials/web-push",
      { subject },
      undefined,
      true,
      true,
    );
  }

  public async deleteCredential(provider: PushProviderStatus["provider"]): Promise<void> {
    await this.deleteRequest(`/push/credentials/${provider}`);
  }

  public async saveDeviceSource(
    provider: PushProviderStatus["provider"],
    source: DeviceSourceConfig,
  ): Promise<unknown> {
    return this.putRequest<DeviceSourceConfig, unknown>(
      `/push/credentials/${provider}/device-source`,
      source,
      undefined,
      true,
      true,
    );
  }

  public async getCategories(): Promise<PushCategoriesResponse> {
    return this.getRequest<PushCategoriesResponse>("/push/categories");
  }

  public async upsertCategory(input: {
    category: string;
    label?: string;
    description?: string;
    is_enabled: boolean;
    // Admin-edited per-action labels. Only ids the app has registered
    // (present in IosActionOrder) are kept server-side; the rest are dropped.
    ios_labels?: Record<string, string>;
  }): Promise<{ id: number; category: string }> {
    return this.putRequest<typeof input, { id: number; category: string }>(
      "/push/categories",
      input,
      undefined,
      true,
      true,
    );
  }

  public async renameCategory(id: number, newCategory: string): Promise<RenameCategoryResponse> {
    return this.putRequest<{ new_category: string }, RenameCategoryResponse>(
      `/push/categories/${id}/rename`,
      { new_category: newCategory },
      undefined,
      true,
      true,
    );
  }

  public async getCategoryReferences(id: number): Promise<CategoryReferencesResponse> {
    return this.getRequest<CategoryReferencesResponse>(`/push/categories/${id}/references`);
  }

  public async deleteCategory(id: number): Promise<void> {
    await this.deleteRequest(`/push/categories/${id}`);
  }

  // Same endpoint the Flutter app calls on boot — used here from the admin UI
  // to seed presets so they appear in dropdowns without waiting on the app side.
  public async syncCategories(input: {
    app_version?: string;
    categories: Array<{
      id: string;
      actions: Array<{
        id: string;
        label: string;
        destructive?: boolean;
        opens_app?: boolean;
      }>;
    }>;
  }): Promise<unknown> {
    return this.postRequest<typeof input, unknown>(
      "/push/categories/sync",
      input,
      undefined,
      true,
      true,
    );
  }

  public async getTemplates(): Promise<PushTemplatesResponse> {
    return this.getRequest<PushTemplatesResponse>("/push/templates");
  }

  public async createTemplate(input: SaveTemplateInput): Promise<PushTemplate> {
    return this.postRequest<SaveTemplateInput, PushTemplate>(
      "/push/templates",
      input,
      undefined,
      true,
      true,
    );
  }

  public async updateTemplate(id: number, input: SaveTemplateInput): Promise<PushTemplate> {
    return this.putRequest<SaveTemplateInput, PushTemplate>(
      `/push/templates/${id}`,
      input,
      undefined,
      true,
      true,
    );
  }

  public async deleteTemplate(id: number): Promise<void> {
    await this.deleteRequest(`/push/templates/${id}`);
  }

  public async getDevices(includeUnregistered = false): Promise<PushDevicesResponse> {
    const q = includeUnregistered ? "?includeUnregistered=true" : "";
    return this.getRequest<PushDevicesResponse>(`/push/devices${q}`);
  }

  public async removeDevice(id: number): Promise<void> {
    await this.deleteRequest(`/push/devices/${id}`);
  }

  public async sendTest(input: {
    template_type?: string;
    title?: string;
    body?: string;
    image_url?: string;
    click_action?: string;
    category?: string;
    buttons?: Array<{ id: string; label: string; click_action?: string }>;
    target_user_id?: number;
    target_user_ids?: number[];
    target_token?: string;
  }): Promise<unknown> {
    return this.postRequest<typeof input, unknown>(
      "/push/test",
      input,
      undefined,
      true,
      true,
    );
  }

  public async listSendLogs(page = 1, pageSize = 25): Promise<PushSendLogsPage> {
    return this.getRequest<PushSendLogsPage>(
      `/push/send-logs?page=${page}&pageSize=${pageSize}`,
    );
  }

  public async getSendLog(id: number): Promise<PushSendLog> {
    return this.getRequest<PushSendLog>(`/push/send-logs/${id}`);
  }
}

export const tenantPushService = new TenantPushService();
